import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import styles from './TrendSpark.module.css';

export interface TrendSparkProps {
  values: number[];
  /** One label per value ("Q4 2024"), shown in the hover tooltip. */
  labels: string[];
  /** Stroke / dot / value colour — a token, e.g. var(--metric-acquired). */
  color: string;
  /** Formats the hovered value ("$84M"). */
  format: (value: number) => string;
  /** Fixed width; omit to fill the container (measured, so the dot stays round). */
  width?: number;
  height?: number;
}

/**
 * A sparkline you can read: d360's widget trend — gradient area under the
 * line, and on hover a guide line, a dot on the curve, and a tooltip with the
 * value (in the metric colour) and its quarter. Arrow keys step through points
 * when focused, so the readout isn't pointer-only.
 */
export function TrendSpark({ values, labels, color, format, width: fixedWidth, height = 40 }: TrendSparkProps) {
  const gradientId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [active, setActive] = useState<number | null>(null);
  const [measured, setMeasured] = useState(0);

  useEffect(() => {
    if (fixedWidth !== undefined || !wrapRef.current) return;
    const el = wrapRef.current;
    const ro = new ResizeObserver(([entry]) => setMeasured(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fixedWidth]);

  const width = fixedWidth ?? measured;
  const fluid = fixedWidth === undefined;

  if (values.length < 2) return null;
  if (width <= 0) return <div ref={wrapRef} className={styles.wrap} data-fluid={fluid || undefined} style={{ height }} />;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  const pad = 3; // keep the dot and stroke inside the box
  const pts = values.map((v, i) => [i * step, height - pad - ((v - min) / span) * (height - pad * 2)] as const);

  const line = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width} ${height} L0 ${height} Z`;

  const indexAt = (clientX: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = ((clientX - rect.left) / rect.width) * width;
    return Math.max(0, Math.min(values.length - 1, Math.round(x / step)));
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => setActive(indexAt(e.clientX));
  const onKeyDown = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const from = active ?? values.length - 1;
    setActive(Math.max(0, Math.min(values.length - 1, from + (e.key === 'ArrowRight' ? 1 : -1))));
  };

  const point = active !== null ? pts[active] : null;
  // Tooltip x as a % of the box, clamped so it doesn't hang off either end.
  const tipLeft = point ? Math.max(18, Math.min(82, (point[0] / width) * 100)) : 0;

  return (
    <div
      ref={wrapRef}
      className={styles.wrap}
      data-fluid={fluid || undefined}
      style={{ width: fluid ? undefined : width, height, color }}
    >
      <svg
        ref={svgRef}
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Trend, ${labels[0]} to ${labels[labels.length - 1]}`}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onFocus={() => setActive(values.length - 1)}
        onBlur={() => setActive(null)}
        onKeyDown={onKeyDown}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.45" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} stroke="none" />
        <path d={line} className={styles.line} />
        {point && (
          <>
            <line x1={point[0]} x2={point[0]} y1={0} y2={height} className={styles.guide} />
            <circle cx={point[0]} cy={point[1]} r={4.5} className={styles.dot} />
          </>
        )}
      </svg>

      {point && active !== null && (
        <div className={styles.tip} style={{ left: `${tipLeft}%` }} role="status">
          <span className={styles.tipValue}>{format(values[active])}</span>
          <span className={styles.tipLabel}>{labels[active]}</span>
        </div>
      )}
    </div>
  );
}
